import { useEffect, useState } from "react";
import posthog from "posthog-js";

export type FeatureFlag = {
  /** False until PostHog's flags load, or the timeout fires. */
  resolved: boolean;
  value: string | boolean | undefined;
};

// Resolves a flag once PostHog's flags load, guarding against onFeatureFlags
// re-firing. A timeout falls back to resolved-with-no-value so a blocked or slow
// PostHog never holds the caller forever. The load path reads the flag, firing
// the experiment's $feature_flag_called exposure; the timeout path does not, so
// those visitors are simply left unenrolled.
export const useFeatureFlag = (
  flagKey: string,
  timeoutMs = 1000,
): FeatureFlag => {
  const [flag, setFlag] = useState<FeatureFlag>({
    resolved: false,
    value: undefined,
  });

  useEffect(() => {
    let done = false;
    let unsubscribe: (() => void) | undefined;
    // onFeatureFlags fires synchronously when flags are already cached, so
    // `resolve` can run during the subscribe call below — `timer` must already
    // exist, hence a `let` set before subscribing rather than a later `const`.
    let timer: number | undefined;
    const resolve = (value: string | boolean | undefined) => {
      if (done) return;
      done = true;
      unsubscribe?.();
      window.clearTimeout(timer);
      setFlag({ resolved: true, value });
    };
    timer = window.setTimeout(() => resolve(undefined), timeoutMs);
    unsubscribe = posthog.onFeatureFlags(() =>
      resolve(posthog.getFeatureFlag(flagKey)),
    );
    return () => {
      unsubscribe?.();
      window.clearTimeout(timer);
    };
  }, [flagKey, timeoutMs]);

  return flag;
};

/**
 * One-shot, non-hook flag read for callers that decide *when* to enroll a
 * visitor. Same load/timeout semantics as `useFeatureFlag`: resolves with the
 * flag's value once flags load, or `undefined` on timeout or abort.
 */
export const resolveFeatureFlag = (
  flagKey: string,
  signal: AbortSignal,
  timeoutMs = 1000,
): Promise<string | boolean | undefined> =>
  new Promise((resolve) => {
    let done = false;
    let unsubscribe: (() => void) | undefined;
    let timer: number | undefined;
    const finish = (value: string | boolean | undefined) => {
      if (done) return;
      done = true;
      unsubscribe?.();
      window.clearTimeout(timer);
      resolve(value);
    };
    signal.addEventListener("abort", () => finish(undefined));
    timer = window.setTimeout(() => finish(undefined), timeoutMs);
    unsubscribe = posthog.onFeatureFlags(() =>
      finish(posthog.getFeatureFlag(flagKey)),
    );
  });
